/*
  Copyright (c) 2018-present, Accuracy.
  All rights reserved.
*/
import {CircularProgress, createStyles, StyleRules, Tab, Tabs, Theme} from "@material-ui/core";
import makeStyles from '@material-ui/core/styles/makeStyles';
import classNames from 'classnames';
import React, {memo, useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {useLocation} from "react-router";
import Gallery from "./Gallery";
import {apiUrl} from "../../utils/api";

type GalleryTile = {img: string, title: string, cols?: number};
type GalleriesResponse = {nature: GalleryTile[], urban: GalleryTile[], myWorld: GalleryTile[]};

//----------------------------------------------------------------------------------------------------------------------
//- Styles
//----------------------------------------------------------------------------------------------------------------------
export type GalleriesClassKey = 'root' | 'tabsWrapper' | 'indicator' | 'tab' | 'tabSelected' | 'panel' | 'loading';

const useStyles = makeStyles<Theme, {}, GalleriesClassKey>(
  (): StyleRules<GalleriesClassKey> =>
    createStyles({
      root: {},
      tabsWrapper: {
        // Sticks right under the TopBar (66px tall) so the two form a
        // continuous frosted band that the photo grid scrolls beneath.
        position: 'sticky',
        top: '66px',
        zIndex: 150,
        padding: '0 clamp(20px, 4vw, 56px)',
        background: 'rgba(11,11,12,.72)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderBottom: '1px solid var(--color-rule-header)'
      },
      indicator: {
        backgroundColor: 'var(--color-text-strong)'
      },
      tab: {
        color: 'var(--color-text-muted)',
        fontFamily: 'var(--font-body)',
        fontSize: '11.5px',
        letterSpacing: '.16em',
        textTransform: 'uppercase'
      },
      tabSelected: {
        color: 'var(--color-text-strong)'
      },
      panel: {
        padding: 'clamp(24px, 5vh, 48px) 0 clamp(48px, 8vh, 96px)'
      },
      loading: {
        display: 'flex',
        justifyContent: 'center',
        padding: 'clamp(48px, 10vh, 120px) 0'
      }
    }),
  {
    name: 'Galleries'
  });

//----------------------------------------------------------------------------------------------------------------------
//- Component
//----------------------------------------------------------------------------------------------------------------------
export type GalleriesProps = {
  className?: string;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
  className?: string;
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function TabPanel({children, value, index, className}: TabPanelProps) {
  if (value !== index) {
    return null;
  }

  return (
    <div
      role="tabpanel"
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      className={className}
    >
      {children}
    </div>
  );
}

function a11yProps(index: any) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}


export default memo(({className}: GalleriesProps) => {
  // hooks
  const classes = useStyles({});
  const {t} = useTranslation('common');
  let query = useQuery();

  const [value, setValue] = useState(Number(query.get("id")) || 0);
  const [galleries, setGalleries] = useState<GalleriesResponse | null>(null);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    let cancelled = false;

    fetch(apiUrl('api/getGalleries.php'))
    .then((response) => response.json())
    .then((d: GalleriesResponse) => {
      if (!cancelled) {
        setGalleries(d);
      }
    })
    .catch(() => {
      if (!cancelled) {
        setGalleries({nature: [], urban: [], myWorld: []});
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.tabsWrapper}>
        <Tabs
          centered
          value={value}
          onChange={handleChange}
          aria-label="simple tabs example"
          classes={{indicator: classes.indicator}}
        >
          <Tab classes={{root: classes.tab, selected: classes.tabSelected}} label={t('home.nature')} {...a11yProps(0)} />
          <Tab classes={{root: classes.tab, selected: classes.tabSelected}} label={t('home.urban')} {...a11yProps(1)} />
          <Tab classes={{root: classes.tab, selected: classes.tabSelected}} label={t('home.myWorld')} {...a11yProps(2)} />
        </Tabs>
      </div>
      {!galleries ? (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      ) : (
        <>
          <TabPanel value={value} index={0} className={classes.panel}>
            <Gallery tileData={galleries.nature} />
          </TabPanel>
          <TabPanel value={value} index={1} className={classes.panel}>
            <Gallery tileData={galleries.urban} />
          </TabPanel>
          <TabPanel value={value} index={2} className={classes.panel}>
            <Gallery tileData={galleries.myWorld} />
          </TabPanel>
        </>
      )}
    </div>
  );
});
